/** Toda pessoa com sessão, da maior para a menor visão. */
export const PERSONAS_HUMANAS = ['super_admin', 'executivo', 'admin_parceiro', 'gestor', 'usuario'] as const;
export type HumanPersona = (typeof PERSONAS_HUMANAS)[number];
export type Persona = HumanPersona | 'parceiro_api' | 'sistema';

/** Único lugar do código que conhece `roles.name` (lint proíbe esses literais fora daqui). */
const ROLE_TO_PERSONA = {
  'Super-Administrador': 'super_admin',
  'Executivo de contas': 'executivo',
  Administrador: 'admin_parceiro',
  Gestor: 'gestor',
  Usuario: 'usuario',
} as const satisfies Record<string, HumanPersona>;

/** Equipe da Aliança: vê todos os parceiros e vende por eles. Configurar o sistema continua só do `super_admin`. */
export const PERSONAS_DA_ALIANCA = ['super_admin', 'executivo'] as const satisfies readonly HumanPersona[];

/** Papéis de dentro de um parceiro. Com `PERSONAS_DA_ALIANCA`, divide `PERSONAS_HUMANAS` (personas.test.ts confere). */
export const PERSONAS_DE_PARCEIRO = ['admin_parceiro', 'gestor', 'usuario'] as const satisfies readonly HumanPersona[];

const DA_ALIANCA: ReadonlySet<Persona> = new Set<Persona>(PERSONAS_DA_ALIANCA);
const DE_PARCEIRO: ReadonlySet<Persona> = new Set<Persona>(PERSONAS_DE_PARCEIRO);

/** Quem tem carteira de parceiros (CARTEIRA.md D2): assume parceiro e perde a carteira ao sair do papel. */
export const PERSONAS_COM_CARTEIRA = ['executivo'] as const satisfies readonly HumanPersona[];
const COM_CARTEIRA: ReadonlySet<Persona> = new Set<Persona>(PERSONAS_COM_CARTEIRA);

export const ehDaAlianca = (persona: Persona | null | undefined): boolean => persona != null && DA_ALIANCA.has(persona);
export const temCarteira = (persona: Persona | null | undefined): boolean => persona != null && COM_CARTEIRA.has(persona);
/** Trocar `de` por `para` fecha a carteira (domínio e aviso da tela usam esta regra). */
export const perdeCarteira = (de: Persona | null, para: Persona): boolean => temCarteira(de) && !temCarteira(para);
export const ehDeParceiro = (persona: Persona | null | undefined): boolean => persona != null && DE_PARCEIRO.has(persona);

/**
 * Presa às boas-vindas: só pessoas do parceiro em onboarding (a equipe da Aliança vinculada a ele, não). A API bloqueia e
 * o `/me` redireciona pela mesma regra.
 */
export const presaAoOnboarding = (persona: Persona | null | undefined, etapaOnboarding: string | null): boolean => ehDeParceiro(persona) && etapaOnboarding !== null;

/** Dar ou tirar um papel da Aliança pede a senha de quem age (domínio e tela usam esta regra). */
export const papelPedeSenha = (personas: ReadonlyArray<HumanPersona | null>): boolean => personas.some(ehDaAlianca);

/** `roles.name` de uma persona (filtro da lista de usuários). */
export function roleNameDaPersona(persona: HumanPersona): string {
  return Object.entries(ROLE_TO_PERSONA).find(([, p]) => p === persona)![0];
}

/** Case-sensitive e sem `trim`: nome fora do mapa (inclusive 'Consultor' e NULL) ⇒ `null` ⇒ 403. */
export function personaFromRoleName(name: string | null): HumanPersona | null {
  return name !== null && Object.hasOwn(ROLE_TO_PERSONA, name)
    ? ROLE_TO_PERSONA[name as keyof typeof ROLE_TO_PERSONA]
    : null;
}

/** Rótulo de tela da persona; a UI nunca exibe `roles.name` cru ("Usuario" sem acento). */
export const PERSONA_LABEL: Record<HumanPersona, string> = {
  super_admin: 'Super-Administrador',
  executivo: 'Executivo de contas',
  admin_parceiro: 'Administrador',
  gestor: 'Gestor',
  usuario: 'Consultor',
};
